// src/components/common/Footer.tsx
import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Link, 
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link as RouterLink } from 'react-router-dom';

const Footer: React.FC = () => {
  const theme = useTheme();

  return (
    <Box component="footer" sx={{ 
      py: 8,
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`
    }}>
      <Container maxWidth="xl">
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          mb: 8,
          justifyContent: 'space-between'
        }}>
          {/* Company Info */}
          <Box sx={{ maxWidth: 300 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              MWM Delivery
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Connecting businesses with customers through fast, reliable delivery services.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {[FacebookIcon, TwitterIcon, InstagramIcon, LinkedInIcon].map((Icon, index) => (
                <IconButton
                  key={index}
                  color="inherit"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Links Sections */}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'space-between'
          }}>
            {[
              {
                title: 'Vendors',
                links: [
                  { label: 'Join as Vendor', to: '/vendor/register' },
                  { label: 'Vendor Login', to: '/vendor/login' },
                  { label: 'Resources', to: '/vendor/resources' },
                  { label: 'Vendor Support', to: '/vendor/support' }
                ]
              },
              {
                title: 'Company',
                links: [
                  { label: 'About Us', to: '/about' },
                  { label: 'Careers', to: '/careers' },
                  { label: 'Press', to: '/press' },
                  { label: 'Contact Us', to: '/contact' }
                ]
              },
              {
                title: 'Legal',
                links: [
                  { label: 'Terms of Service', to: '/terms' },
                  { label: 'Privacy Policy', to: '/privacy' },
                  { label: 'Cookie Policy', to: '/cookies' },
                  { label: 'Legal Notices', to: '/legal' }
                ]
              }
            ].map((section, index) => (
              <Box key={index}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {section.title}
                </Typography>
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    component={RouterLink}
                    to={link.to}
                    color="text.secondary"
                    display="block"
                    gutterBottom
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'divider', my: 4 }} />

        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} MWM Delivery. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;